import type { CreditReportType, FullCreditProfile, TradeLine } from '@zg-rentals/ts-capps-client';
import { getCurrencyDisplayFromDollars } from '@zg-rentals/formatters';
import {
  getExperianCurrencyDisplay,
  getNumberFromString,
  getOnTimePayments,
  getTotalPayments,
  isCollection,
  isOpenAccount,
} from './credit-report-utils';

export const getOnTimePaymentsPercent = (creditReport: {
  fullCreditProfile?: Pick<FullCreditProfile, 'TradeLine'>;
}) => {
  const { fullCreditProfile } = creditReport;

  if (fullCreditProfile?.TradeLine && fullCreditProfile.TradeLine.length) {
    let totalPayments = 0;
    let totalOnTimePayments = 0;

    fullCreditProfile.TradeLine.forEach((tradeline) => {
      if (!isCollection(tradeline)) {
        const paymentProfile = tradeline.PaymentProfile;
        if (typeof paymentProfile === 'string') {
          totalPayments += getTotalPayments(paymentProfile);
          totalOnTimePayments += getOnTimePayments(paymentProfile);
        }
      }
    });

    return getFormattedPercentage(totalOnTimePayments, totalPayments);
  }
  return '';
};

export const getEstimatedMonthlyPayments = (creditReport: {
  fullCreditProfile?: Pick<FullCreditProfile, 'TradeLine'>;
}) => {
  const { fullCreditProfile } = creditReport;

  if (fullCreditProfile && fullCreditProfile.TradeLine && fullCreditProfile.TradeLine.length) {
    const { TradeLine: tradeLine } = fullCreditProfile;
    const numberAmount = tradeLine.reduce((sum, trade) => {
      if (!isCollection(trade) && (isOpenAccount(trade) || isClosedWithBalance(trade))) {
        const { MonthlyPaymentAmount: amount } = trade;

        sum += getNumberFromString(amount);
      }
      return sum;
    }, 0);
    return getExperianCurrencyDisplay(numberAmount.toString());
  }
  return '';
};

export const getCreditUsed = (creditReport: CreditReportType) => {
  const { fullCreditProfile } = creditReport;

  if (fullCreditProfile && fullCreditProfile.TradeLine && fullCreditProfile.TradeLine.length) {
    let totalNumerator = 0;
    let totalDenominator = 0;

    fullCreditProfile.TradeLine.forEach((trade) => {
      let denominator, numerator;
      if (!isCollection(trade) && (isOpenAccount(trade) || isClosedWithBalance(trade))) {
        const { RevolvingOrInstallment: tradeType, Amount: amounts, BalanceAmount } = trade;
        const isInstallment = tradeType?.desc === 'Installment';

        if (isInstallment) {
          return;
        }

        let accountLimit = '';

        numerator = getNumberFromString(BalanceAmount);

        if (amounts && Array.isArray(amounts)) {
          amounts.forEach((amount) => {
            const { Qualifier: qualifier } = amount;

            if (qualifier) {
              const { code } = qualifier;
              /**
               * denominator is credit limit if available, high balance if not.
               */
              if (code === 'L' || (code === 'H' && !accountLimit)) {
                accountLimit = amount.Value;
              }
            }
          });
        }
        denominator = getNumberFromString(accountLimit);

        if (!isNaN(numerator) && !isNaN(denominator)) {
          totalNumerator += numerator;
          totalDenominator += denominator;
        }
      }
    });

    return getFormattedPercentage(totalNumerator, totalDenominator);
  }
  return '';
};

export const getTotalDebt = (creditReport: CreditReportType) => {
  const { fullCreditProfile } = creditReport;

  if (fullCreditProfile && fullCreditProfile.TradeLine && fullCreditProfile.TradeLine.length) {
    const { TradeLine: tradeLine } = fullCreditProfile;
    const numberAmount = tradeLine.reduce((sum, trade) => {
      if (!isCollection(trade) && (isOpenAccount(trade) || isClosedWithBalance(trade))) {
        if (trade.BalanceAmount) {
          const { BalanceAmount: amount } = trade;
          sum += getNumberFromString(amount);
        } else {
          return sum;
        }
      }
      return sum;
    }, 0);
    return getCurrencyDisplayFromDollars({ amount: numberAmount, hideCents: true });
  }
  return '';
};

const getFormattedPercentage = (numerator: number, denominator: number) => {
  const percentage = denominator ? Math.round((numerator / denominator) * 100) : 0;
  return `${percentage}%`;
};

const isClosedWithBalance = (trade: TradeLine) => {
  let closedWithBalance = false;

  if (trade.EnhancedPaymentData && trade.EnhancedPaymentData.AccountCondition && trade.BalanceAmount) {
    if (trade.EnhancedPaymentData.AccountCondition.code === 'A3' && getNumberFromString(trade.BalanceAmount) > 0) {
      closedWithBalance = true;
    }
  }

  if (
    trade.EnhancedPaymentData &&
    trade.EnhancedPaymentData.PaymentStatus &&
    trade.EnhancedPaymentData.PaymentStatus.code &&
    trade.BalanceAmount
  ) {
    if (trade.EnhancedPaymentData.PaymentStatus.code === 'A3' && getNumberFromString(trade.BalanceAmount) > 0) {
      closedWithBalance = true;
    }
  }

  return closedWithBalance;
};
